import { useMemo, useState, type ReactNode } from 'react';
import {
  CCY_SORT_ORDER,
  D,
  AMT_UI_SCALE,
  MONEY_SCALE,
  fmtAmt,
  type CcyCode,
  type ChatBalanceSource,
  type Money,
} from '@wave/shared';
import { useChatBalances, useUnmatched, useUsdtRates } from '../api/hooks';
import { hasRight, useAuthStore } from '../auth/store';
import { btnSecondarySm, pageContainer, pageHeader, pageSubtitle, pageTitle } from '../lib/styles';
import { ChatBalancesConfigModal } from '../components/modals/ChatBalancesConfigModal';
import { TronWalletsModal } from '../components/modals/TronWalletsModal';
import { IconRefresh, IconSettings } from '../components/icons';
import { Sparkline } from '../components/Sparkline';
import { useBalanceHistory, useBalanceHistoryByChat } from '../components/balances/useBalanceHistory';
import { deltaOverWindow, fmtAgo, lastPointAgeMs } from '../components/balances/historyMath';
import {
  bucketLabelToMinutes,
  confirmDivergences,
  detectDivergences,
  divergenceMatchWindow,
  fmtNum,
  type Divergence,
} from '../components/balances/trendMath';
import { ChartsPanel } from '../components/balances/ChartsPanel';
import { UntrackedWalletsBanner } from '../components/balances/UntrackedWalletsBanner';
import { ChatHistoryModal } from '../components/balances/ChatHistoryModal';
import { BalanceChangesLog } from '../components/balances/BalanceChangesLog';
import { KpiHint } from '../components/balances/KpiHint';
import { buildKpiHints } from '../components/balances/kpiHints';
import { staleCcys } from '../components/balances/staleCcy';

type Kind = 'client' | 'executor';
type FilterKind = 'all' | Kind;

interface Row {
  chatId: string;
  title: string;
  accountCode: string | null;
  accountKind: Kind | null;
  // Чат с обратной знаковой моделью: суммы уже инвертированы сервером,
  // здесь флаг — только для бейджа.
  inverted: boolean;
  // Источник строки: внешний API, распарсенные листенером сообщения или
  // опрошенный TRON-кошелёк.
  source: 'api' | 'telegram' | 'tron';
  // Настройка «кто хозяин остатков» (null — чат не из listening_chats).
  // Отличается от `source`: там фактический источник цифр, здесь — выбор.
  balanceSource: ChatBalanceSource | null;
  tgUpdatedAt: string | null;
  ccyMap: Map<CcyCode, Money>;
  netUsdt: string; // signed, округлён до MONEY_SCALE — для отображения/сортировки
  netUsdtFull: string; // signed, без округления — для KPI-агрегации
  // Валюты чата с ненулевым остатком, для которых нет курса USDT. Если непусто —
  // net по чату НЕ определён; рендерим прочерк + бейдж «нет курса».
  missingRateCcys: CcyCode[];
  // Валюты, «залипшие» намного дольше остальных валют этого же чата (см.
  // staleCcys) — страховка на случай нераспознанного формата снимка баланса.
  staleByCcy: Map<string, number>;
}

type SortKey = 'title' | 'netUsdt' | CcyCode;

export function BalancesPage() {
  const { data, isLoading, error, refetch, isFetching } = useChatBalances();
  const { data: rates } = useUsdtRates();
  const { data: history7d } = useBalanceHistory('7d');
  const { data: byChat7d } = useBalanceHistoryByChat('7d');
  const canManage = hasRight(
    useAuthStore((s) => s.rights),
    'chat-balances.manage',
  );

  const [showConfig, setShowConfig] = useState(false);
  const [showTronModal, setShowTronModal] = useState(false);
  const [chatModal, setChatModal] = useState<{ chatId: string; title: string } | null>(null);
  const [filter, setFilter] = useState<FilterKind>('all');
  const [search, setSearch] = useState('');
  const [showEmpty, setShowEmpty] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'netUsdt',
    dir: 'asc',
  });

  const sparkByChat = useMemo(() => {
    const m = new Map<string, number[]>();
    for (const c of byChat7d?.chats ?? []) m.set(c.chatId, c.points.map((p) => Number(p.netUsdt)));
    return m;
  }, [byChat7d]);

  // Незакрытое расхождение позиции («деньги, возможно, потерялись») — на
  // уровне страницы, а не внутри TrendChart (панель графиков по умолчанию
  // свёрнута, там алерт был бы фактически невидим, см. I3 финального
  // ревью). Переиспользуем уже загруженную history7d (та же, что у KpiBar) —
  // отдельный запрос не нужен. bucketMinutes — из фактического bucket
  // ответа API, не хардкод по диапазону.
  const historyPoints = history7d?.points ?? [];
  const rawDivergences = useMemo(() => {
    const bucketMinutes = bucketLabelToMinutes(history7d?.bucket);
    return detectDivergences(historyPoints, { bucketMinutes });
  }, [historyPoints, history7d?.bucket]);
  // Плашка показывается только для ПОДТВЕРЖДЁННЫХ сверкой с журналом `open`
  // (см. trendMath.ts: `confirmDivergences`) — окно сверки строим по
  // кандидатам, не по всей истории 7д (иначе рискуем упереться в
  // предохранитель API, `truncated`, на широких окнах).
  const matchWindow = useMemo(
    () => divergenceMatchWindow(rawDivergences, historyPoints),
    [rawDivergences, historyPoints],
  );
  const { data: unmatched } = useUnmatched(matchWindow?.from ?? null, matchWindow?.to ?? null);
  const openDivergence = useMemo<Divergence | null>(() => {
    // Пока ответ сверки не пришёл — не понижаем `open` до `explained`
    // раньше времени (см. тот же приём в TrendChart.tsx).
    const divs = unmatched ? confirmDivergences(rawDivergences, unmatched.items, unmatched.truncated) : rawDivergences;
    const list = divs.filter((d) => d.status === 'open');
    return list.length > 0 ? list[list.length - 1]! : null;
  }, [rawDivergences, unmatched]);
  const evidenceText = openDivergence ? fmtEvidenceText(openDivergence) : null;

  const ratesMap = useMemo(() => {
    const m: Partial<Record<CcyCode, Money>> = {};
    for (const r of rates ?? []) m[r.ccy] = r.rate;
    return m as Record<CcyCode, Money>;
  }, [rates]);

  const rows: Row[] = useMemo(() => {
    const chats = data?.chats ?? [];
    return chats.map((c) => {
      const ccyMap = new Map<CcyCode, Money>();
      for (const b of c.balances) ccyMap.set(b.ccy, b.amount);
      // Промежуточные конверсии НЕ округляем — округление одно, в конце цепочки.
      let net = D(0);
      const missingRateCcys: CcyCode[] = [];
      ccyMap.forEach((amt, ccy) => {
        const rate = ratesMap[ccy];
        if (!rate || D(rate).lte(0)) {
          if (!D(amt).isZero()) missingRateCcys.push(ccy);
          return;
        }
        net = net.plus(D(amt).dividedBy(D(rate)));
      });
      return {
        chatId: c.chatId,
        title: c.title,
        accountCode: c.accountCode,
        accountKind: c.accountKind,
        inverted: c.inverted,
        source: c.source,
        balanceSource: c.balanceSource,
        tgUpdatedAt: c.updatedAt,
        ccyMap,
        netUsdt: net.toDecimalPlaces(MONEY_SCALE).toString(),
        netUsdtFull: net.toString(),
        missingRateCcys,
        staleByCcy: staleCcys(
          c.balances.map((b) => ({ ccy: b.ccy, amount: b.amount, updatedAt: b.updatedAt })),
        ),
      };
    });
  }, [data, ratesMap]);

  // Visible currency columns — только те, что встречаются хотя бы в одной строке.
  const visibleCcys = useMemo<CcyCode[]>(() => {
    const set = new Set<CcyCode>();
    for (const r of rows) for (const c of r.ccyMap.keys()) set.add(c);
    return [...set].sort((a, b) => (CCY_SORT_ORDER[a] ?? 999) - (CCY_SORT_ORDER[b] ?? 999));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => (showEmpty ? true : r.ccyMap.size > 0))
      .filter((r) => filter === 'all' || r.accountKind === filter)
      .filter(
        (r) =>
          !q ||
          r.title.toLowerCase().includes(q) ||
          (r.accountCode?.toLowerCase().includes(q) ?? false),
      );
  }, [rows, filter, search, showEmpty]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      // Чаты без курса (net не определён) — всегда вниз при сортировке по net.
      if (sort.key === 'netUsdt') {
        const am = a.missingRateCcys.length > 0;
        const bm = b.missingRateCcys.length > 0;
        if (am !== bm) return am ? 1 : -1;
      }
      let cmp = 0;
      if (sort.key === 'title') {
        cmp = a.title.localeCompare(b.title);
      } else if (sort.key === 'netUsdt') {
        cmp = D(a.netUsdt).comparedTo(D(b.netUsdt));
      } else {
        const av = a.ccyMap.get(sort.key);
        const bv = b.ccyMap.get(sort.key);
        const an = av ? D(av).toNumber() : -Infinity;
        const bn = bv ? D(bv).toNumber() : -Infinity;
        cmp = an - bn;
      }
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sort]);

  // KPI — суммируем неокруглённые netUsdtFull, округление одно, в конце.
  // Чаты без курса исключаем (net не определён) и считаем отдельно.
  // TRON-кошельки (source='tron') — активы, в pos/neg/count/net чатов НЕ входят,
  // считаются отдельно в `wallets`; `position` — покрывают ли кошельки чистый долг чатов.
  const kpi = useMemo(() => {
    let pos = D(0);
    let neg = D(0);
    let wallets = D(0);
    let withBalance = 0;
    let excluded = 0;
    for (const r of rows) {
      if (r.source === 'tron') {
        wallets = wallets.plus(D(r.netUsdtFull));
        continue; // кошельки — активы, в pos/neg/count чатов не входят
      }
      if (r.ccyMap.size > 0) withBalance++;
      if (r.missingRateCcys.length > 0) {
        excluded++;
        continue;
      }
      const n = D(r.netUsdtFull);
      if (n.gt(0)) pos = pos.plus(n);
      else if (n.lt(0)) neg = neg.plus(n);
    }
    const net = pos.plus(neg);
    return {
      count: withBalance,
      excluded,
      pos: pos.toDecimalPlaces(2).toString(),
      neg: neg.toDecimalPlaces(2).toString(),
      net: net.toDecimalPlaces(2).toString(),
      wallets: wallets.toDecimalPlaces(2).toString(),
      position: wallets.minus(net).toDecimalPlaces(2).toString(),
    };
  }, [rows]);

  // «Итого (чаты)» — только обязательства по чатам, TRON-кошельки (активы) исключены.
  const ccyTotals = useMemo(() => {
    const totals = new Map<CcyCode, string>();
    for (const ccy of visibleCcys) {
      let s = D(0);
      for (const r of sorted) {
        if (r.source === 'tron') continue;
        const v = r.ccyMap.get(ccy);
        if (v) s = s.plus(v);
      }
      totals.set(ccy, s.toDecimalPlaces(MONEY_SCALE).toString());
    }
    return totals;
  }, [visibleCcys, sorted]);

  function toggleSort(key: SortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  }

  const configured = data?.configured ?? false;
  const upstreamError = data?.error ?? null;
  const stale = data?.stale ?? false;
  const fetchedAtAgo = fmtAgo(data?.fetchedAt ?? null, Date.now());
  // Stale-режим: источник упал, но есть сохранённый снапшот — показываем его.
  const showData = Boolean(data && configured && (!upstreamError || (stale && data.chats.length > 0)));

  return (
    <div className={pageContainer}>
      <div className={pageHeader}>
        <div>
          <h1 className={pageTitle}>Балансы по чатам</h1>
          <p className={pageSubtitle}>
            Реальные остатки по каждому чату из внешнего источника.
            {data?.asOf && (
              <>
                {' '}
                Актуально на <span className="font-medium text-text">{data.asOf}</span>.
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {fetchedAtAgo && (
            <span
              className={`text-[11px] ${stale ? 'text-warning' : 'text-text-subtle'}`}
              title="Когда данные реально получены из источника"
            >
              данные: {fetchedAtAgo}
            </span>
          )}
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className={btnSecondarySm}
            title="Обновить остатки"
          >
            <IconRefresh className={isFetching ? 'animate-spin' : undefined} />
            Обновить
          </button>
          {canManage && (
            <button
              type="button"
              onClick={() => setShowConfig(true)}
              className={btnSecondarySm}
              title="Настроить источник"
            >
              <IconSettings />
              Источник
            </button>
          )}
          {canManage && (
            <button
              type="button"
              onClick={() => setShowTronModal(true)}
              className={btnSecondarySm}
              title="TRON-кошельки (USDT), опрашиваемые через TronScan"
            >
              👛 Кошельки
            </button>
          )}
        </div>
      </div>

      {showConfig && <ChatBalancesConfigModal onClose={() => setShowConfig(false)} />}
      {showTronModal && <TronWalletsModal onClose={() => setShowTronModal(false)} />}

      {chatModal && (
        <ChatHistoryModal
          chatId={chatModal.chatId}
          title={chatModal.title}
          // Настройку берём из живого ответа, а не из состояния модалки —
          // иначе после переключения контрол показывал бы старое значение.
          balanceSource={
            data?.chats.find((c) => c.chatId === chatModal.chatId)?.balanceSource ?? null
          }
          canManage={canManage}
          onClose={() => setChatModal(null)}
        />
      )}

      {isLoading && <div className="text-sm text-text2">Загрузка…</div>}
      {error && (
        <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
          {(error as Error).message}
        </div>
      )}

      {data && !configured && (
        <NotConfiguredState canManage={canManage} onConfigure={() => setShowConfig(true)} />
      )}

      {data && configured && upstreamError && !showData && (
        <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
          Внешний источник недоступен: {upstreamError}
        </div>
      )}

      {showData && data && (
        <>
          {stale && upstreamError && (
            <div className="mb-4 rounded-md border border-[var(--orange-border,#fdba74)] bg-[var(--orange-bg,#fff7ed)] px-3 py-2 text-sm text-warning">
              ⚠ Источник недоступен — показаны сохранённые данные ({fetchedAtAgo ?? '—'}).{' '}
              {upstreamError}
            </div>
          )}

          {openDivergence && (
            <div className="mb-4 rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
              ⚠ Расхождение {fmtNum(openDivergence.delta)} USDT открыто с{' '}
              {new Date(openDivergence.ts).toLocaleString('ru-RU')} (
              {fmtDurationSince(openDivergence.ts, Date.now())}) — возможно, вторая нога операции не
              проведена или источник баланса завис.
              {evidenceText && <> {evidenceText}</>}
            </div>
          )}

          <UntrackedWalletsBanner onManageWallets={canManage ? () => setShowTronModal(true) : undefined} />

          <KpiBar kpi={kpi} history={history7d} />

          <ChartsPanel
            liveChats={data.chats}
            onOpenChat={(chatId, title) => setChatModal({ chatId, title })}
          />

          <BalanceChangesLog />

          {/* Toolbar */}
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <SegmentedFilter value={filter} onChange={setFilter} rows={rows} showEmpty={showEmpty} />
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <SearchIcon />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск по чату или аккаунту…"
                className="block h-9 w-full rounded-md border border-border bg-bg pl-8 pr-3 text-sm text-text outline-none transition-colors duration-150 hover:border-border-strong focus:border-accent focus:shadow-focus placeholder:text-text-subtle"
              />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-text2">
              <input
                type="checkbox"
                checked={showEmpty}
                onChange={(e) => setShowEmpty(e.target.checked)}
              />
              Показывать пустые
            </label>
            {/* На телефоне заголовков таблицы нет — сортировка отдельным контролом. */}
            <MobileSort sort={sort} ccys={visibleCcys} onChange={setSort} />
            <div className="ml-auto text-xs text-text2">
              <span className="font-mono font-medium text-text">{sorted.length}</span> из{' '}
              <span className="font-mono">{rows.length}</span>
            </div>
          </div>

          {/* Table */}
          {sorted.length === 0 ? (
            <EmptyState rowsCount={rows.length} />
          ) : (
            <>
            {/* Телефон: карточки вместо широкой таблицы — та же информация, без
                горизонтального скролла. Таблица — с md (≥768px). */}
            <div className="space-y-2 md:hidden">
              {sorted.map((r) => (
                <ChatCard
                  key={r.chatId}
                  row={r}
                  ccys={visibleCcys}
                  spark={sparkByChat.get(r.chatId)}
                  onOpen={() => setChatModal({ chatId: r.chatId, title: r.title })}
                />
              ))}
              <TotalsCard ccys={visibleCcys} totals={ccyTotals} net={kpi.net} />
            </div>
            <div className="hidden overflow-x-auto rounded-lg border border-border bg-bg2 md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-bg3 text-text2">
                    <SortHeader
                      label="Чат"
                      align="left"
                      active={sort.key === 'title'}
                      dir={sort.dir}
                      onClick={() => toggleSort('title')}
                      sticky
                    />
                    <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide">
                      Тип
                    </th>
                    <th className="px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide">
                      Динамика 7д
                    </th>
                    {visibleCcys.map((c) => (
                      <SortHeader
                        key={c}
                        label={c}
                        align="right"
                        active={sort.key === c}
                        dir={sort.dir}
                        onClick={() => toggleSort(c)}
                      />
                    ))}
                    <SortHeader
                      label="Net USDT"
                      align="right"
                      active={sort.key === 'netUsdt'}
                      dir={sort.dir}
                      onClick={() => toggleSort('netUsdt')}
                    />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => (
                    <ChatRow
                      key={r.chatId}
                      row={r}
                      ccys={visibleCcys}
                      spark={sparkByChat.get(r.chatId)}
                      onOpen={() => setChatModal({ chatId: r.chatId, title: r.title })}
                    />
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-border bg-bg3 font-semibold">
                    <td className="sticky left-0 z-[1] bg-bg3 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-text2">
                      Итого (чаты)
                    </td>
                    <td className="px-3 py-2.5" />
                    <td className="px-3 py-2.5" />
                    {visibleCcys.map((c) => {
                      const t = ccyTotals.get(c) ?? '0';
                      return (
                        <td
                          key={c}
                          className={`px-3 py-2.5 text-right font-mono font-semibold tabular-nums ${totalCls(t)}`}
                        >
                          {D(t).isZero() ? '—' : fmtAmt(t, c)}
                        </td>
                      );
                    })}
                    <td className={`px-3 py-2.5 text-right font-mono font-bold tabular-nums ${totalCls(kpi.net)}`}>
                      {fmtSignedUsdt(kpi.net)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// KPI bar (спарклайны 7д + дельта Δ24ч + индикатор свежести снапшотов истории)
// ============================================================================

function KpiBar({
  kpi,
  history,
}: {
  kpi: {
    count: number;
    excluded: number;
    pos: string;
    neg: string;
    net: string;
    wallets: string;
    position: string;
  };
  history: import('@wave/shared').ChatBalancesHistory | undefined;
}) {
  const negAbs = D(kpi.neg).abs().toDecimalPlaces(2).toString();
  const netD = D(kpi.net);
  const positionD = D(kpi.position);
  // Подсказки ⓘ: числа в формулах — тот же formatUsdtNum, что на карточках.
  const hints = buildKpiHints(kpi, formatUsdtNum);
  const points = history?.points ?? [];
  const now = Date.now();
  const series = {
    pos: points.map((p) => Number(p.posUsdt)),
    negAbs: points.map((p) => Math.abs(Number(p.negUsdt))),
    net: points.map((p) => Number(p.netUsdt)),
    wallets: points.map((p) => Number(p.walletsUsdt)),
    position: points.map((p) => Number(p.walletsUsdt) - Number(p.netUsdt)),
  };
  const d24 = {
    pos: deltaOverWindow(points.map((p) => ({ ts: p.ts, value: p.posUsdt })), 24 * 3600_000, now),
    neg: deltaOverWindow(points.map((p) => ({ ts: p.ts, value: p.negUsdt })), 24 * 3600_000, now),
    net: deltaOverWindow(points.map((p) => ({ ts: p.ts, value: p.netUsdt })), 24 * 3600_000, now),
    wallets: deltaOverWindow(points.map((p) => ({ ts: p.ts, value: p.walletsUsdt })), 24 * 3600_000, now),
    position: deltaOverWindow(
      points.map((p) => ({ ts: p.ts, value: D(p.walletsUsdt).minus(D(p.netUsdt)).toString() })),
      24 * 3600_000,
      now,
    ),
  };
  const ageMs = lastPointAgeMs(points, now);
  const stale = ageMs !== null && ageMs > 30 * 60_000; // 2× интервал снапшотов
  const positionSparkColor = series.position.length > 0 && series.position[series.position.length - 1]! < 0
    ? 'danger'
    : 'success';

  return (
    <div className="mb-5">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 xl:grid-cols-6">
        <KpiCard
          label="Чатов с остатком"
          value={String(kpi.count)}
          valueCls="text-text"
          accent="neutral"
          hint={hints.count}
        />
        {/* Знаковая модель чатов: плюс = «мы должны клиенту» (обязательство, красный),
            минус = «клиент должен нам» (актив, зелёный). Рост «мы должны» — плохо (красный). */}
        <KpiCard
          label="Мы должны"
          value={`${formatUsdtNum(kpi.pos)}`}
          unit="USDT"
          valueCls="text-danger"
          accent="danger"
          spark={<Sparkline values={series.pos} color="danger" height={28} />}
          delta={d24.pos ? { text: fmtDelta(d24.pos), up: !d24.pos.delta.startsWith('-'), goodWhenDown: true } : null}
          hint={hints.pos}
        />
        <KpiCard
          label="Нам должны"
          value={`${formatUsdtNum(negAbs)}`}
          unit="USDT"
          valueCls="text-success"
          accent="success"
          spark={<Sparkline values={series.negAbs} color="success" height={28} />}
          // negUsdt signed ≤ 0: его падение = «нам должны» растёт = хорошо.
          delta={d24.neg ? { text: fmtDelta(invertDelta(d24.neg)), up: d24.neg.delta.startsWith('-'), goodWhenDown: false } : null}
          hint={hints.neg}
        />
        <KpiCard
          label="Чистый остаток"
          value={`${netD.gte(0) ? '+' : '−'}${formatUsdtNum(kpi.net)}`}
          unit="USDT"
          valueCls={netD.isNegative() ? 'text-success' : netD.isPositive() ? 'text-danger' : 'text-text'}
          accent={netD.isNegative() ? 'success' : netD.isPositive() ? 'danger' : 'neutral'}
          spark={<Sparkline values={series.net} color={netD.isNegative() ? 'success' : 'danger'} height={28} showBaseline />}
          delta={d24.net ? { text: fmtDelta(d24.net), up: !d24.net.delta.startsWith('-'), goodWhenDown: true } : null}
          hint={hints.net}
        />
        {/* Кошельки — сумма TRON-балансов (активы), считаются отдельно от обязательств чатов. */}
        <KpiCard
          label="На кошельках"
          value={`${formatUsdtNum(kpi.wallets)}`}
          unit="USDT"
          valueCls="text-text"
          accent="neutral"
          spark={<Sparkline values={series.wallets} color="accent" height={28} />}
          delta={
            d24.wallets
              ? { text: fmtDelta(d24.wallets), up: !d24.wallets.delta.startsWith('-'), goodWhenDown: false }
              : null
          }
          hint={hints.wallets}
        />
        {/* Позиция = кошельки − чистый остаток чатов: покрывают ли кошельки чистый долг. */}
        <KpiCard
          label="Позиция"
          value={`${positionD.gte(0) ? '+' : '−'}${formatUsdtNum(kpi.position)}`}
          unit="USDT"
          valueCls={positionD.isNegative() ? 'text-danger' : 'text-success'}
          accent={positionD.isNegative() ? 'danger' : 'success'}
          spark={<Sparkline values={series.position} color={positionSparkColor} height={28} showBaseline />}
          delta={
            d24.position
              ? { text: fmtDelta(d24.position), up: !d24.position.delta.startsWith('-'), goodWhenDown: false }
              : null
          }
          hint={hints.position}
        />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px]">
        {ageMs !== null && (
          <span className={stale ? 'text-warning' : 'text-text-subtle'}>
            {stale ? '⚠ история не обновляется · ' : ''}снапшот {Math.round(ageMs / 60_000)} мин назад
          </span>
        )}
        {kpi.excluded > 0 && (
          <span className="text-warning">
            ⚠ {kpi.excluded} {kpi.excluded === 1 ? 'чат' : 'чата(ов)'} с валютой без курса USDT не
            учтены в остатках USDT — задайте курсы на /admin/rates.
          </span>
        )}
      </div>
    </div>
  );
}

/** «+123,45 (+2,1%)» из результата deltaOverWindow. */
function fmtDelta(d: { delta: string; pct: string | null }): string {
  const sign = d.delta.startsWith('-') ? '−' : '+';
  const abs = D(d.delta).abs().toNumber().toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  const pct = d.pct === null ? '' : ` (${sign}${D(d.pct).abs().toString()}%)`;
  return `${sign}${abs}${pct}`;
}

/** Для карточки «Нам должны» показываем дельту в терминах модуля (рост долга нам = +). */
function invertDelta(d: { delta: string; pct: string | null }): { delta: string; pct: string | null } {
  return { delta: D(d.delta).negated().toString(), pct: d.pct };
}

/**
 * Длительность «сколько уже открыто» для баннера расхождения: «20 ч» / «2 дн»
 * — тот же разбор величины, что у `fmtAgo` (не плодим отдельный хелпер с
 * такими же порогами минут/часов/дней), только без суффикса «назад» — здесь
 * это уточнение в скобках после даты открытия, а не самостоятельная фраза.
 */
function fmtDurationSince(iso: string, nowMs: number): string {
  return (fmtAgo(iso, nowMs) ?? '').replace(/\s*назад$/, '');
}

/**
 * Хвост баннера расхождения с результатом сверки по журналу: если сверка
 * нашла непарную строку — её реквизиты (подтверждение, а не голословный
 * алерт); если сверка была неполной (`matchIncomplete`, см. trendMath.ts —
 * `truncated` в ответе API) — явная оговорка, что окно проверено не целиком.
 * Иначе (сверка ещё грузится) — `null`, ничего не дописываем.
 */
function fmtEvidenceText(d: Divergence): string | null {
  if (d.evidence) {
    const actor = d.evidence.actor ?? '—';
    return `Крупнейшая неспаренная запись в журнале: ${d.evidence.title}, ${fmtNum(Number(d.evidence.delta))} ${d.evidence.ccy}, ${actor}.`;
  }
  if (d.matchIncomplete) return 'Сверка по журналу неполная — окно слишком большое, пара могла не попасть в выборку.';
  return null;
}

function formatUsdtNum(s: string): string {
  return D(s)
    .abs()
    .toNumber()
    .toLocaleString('ru-RU', { maximumFractionDigits: AMT_UI_SCALE });
}

function KpiCard({
  label,
  value,
  unit,
  valueCls,
  accent,
  spark,
  delta,
  hint,
}: {
  label: string;
  value: string;
  /** Единица после числа; на телефоне — мельче, чтобы сумма влезала в половину ширины. */
  unit?: string;
  valueCls: string;
  accent: 'neutral' | 'success' | 'danger';
  spark?: ReactNode;
  delta?: { text: string; up: boolean; goodWhenDown: boolean } | null;
  hint?: import('../components/balances/kpiHints').KpiHintContent;
}) {
  const accentBar =
    accent === 'success'
      ? 'before:bg-[var(--green)]'
      : accent === 'danger'
        ? 'before:bg-[var(--red)]'
        : 'before:bg-[var(--accent)]';
  const deltaCls =
    delta == null
      ? ''
      : (delta.up ? !delta.goodWhenDown : delta.goodWhenDown)
        ? 'text-success'
        : 'text-danger';
  return (
    <div
      className={`relative min-w-0 overflow-hidden rounded-lg border border-border bg-bg2 p-3 sm:p-4 before:absolute before:left-0 before:top-0 before:h-full before:w-0.5 ${accentBar}`}
    >
      <div className="flex items-center text-[11px] font-medium uppercase tracking-wide text-text2">
        {label}
        {hint && <KpiHint content={hint} />}
      </div>
      <div className={`mt-1.5 break-words font-mono text-lg font-bold tabular-nums tracking-tight sm:text-xl ${valueCls}`}>
        {value}
        {unit && (
          <>
            {' '}
            <span className="text-xs opacity-70 sm:text-[length:inherit] sm:opacity-100">{unit}</span>
          </>
        )}
      </div>
      {delta && (
        <div className={`mt-0.5 font-mono text-[11px] tabular-nums ${deltaCls}`} title="Изменение за 24 часа">
          {delta.up ? '▲' : '▼'} {delta.text} · 24ч
        </div>
      )}
      {spark && <div className="mt-2">{spark}</div>}
    </div>
  );
}

// ============================================================================
// Filter pills
// ============================================================================

function SegmentedFilter({
  value,
  onChange,
  rows,
  showEmpty,
}: {
  value: FilterKind;
  onChange: (v: FilterKind) => void;
  rows: Row[];
  showEmpty: boolean;
}) {
  const counts = useMemo(() => {
    let all = 0;
    let client = 0;
    let executor = 0;
    for (const r of rows) {
      if (!showEmpty && r.ccyMap.size === 0) continue;
      all++;
      if (r.accountKind === 'client') client++;
      else if (r.accountKind === 'executor') executor++;
    }
    return { all, client, executor };
  }, [rows, showEmpty]);

  const items: { key: FilterKind; label: string; count: number }[] = [
    { key: 'all', label: 'Все', count: counts.all },
    { key: 'client', label: 'Клиенты', count: counts.client },
    { key: 'executor', label: 'Исполнители', count: counts.executor },
  ];

  return (
    <div className="inline-flex rounded-md border border-border bg-bg p-0.5">
      {items.map((it) => {
        const active = value === it.key;
        return (
          <button
            key={it.key}
            onClick={() => onChange(it.key)}
            className={`inline-flex h-8 items-center gap-1.5 rounded px-3 text-sm font-medium transition-colors duration-150 ${
              active ? 'bg-accent text-white shadow-xs' : 'text-text2 hover:bg-bg2 hover:text-text'
            }`}
          >
            {it.label}
            <span
              className={`rounded px-1 font-mono text-[10px] tabular-nums ${
                active ? 'bg-white/20 text-white' : 'bg-bg3 text-text2'
              }`}
            >
              {it.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-subtle"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// ============================================================================
// Table primitives
// ============================================================================

function SortHeader({
  label,
  align,
  active,
  dir,
  onClick,
  sticky,
}: {
  label: string;
  align: 'left' | 'right';
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: () => void;
  sticky?: boolean;
}) {
  const stickyCls = sticky ? 'sticky left-0 bg-bg3 z-[1]' : '';
  const alignCls = align === 'right' ? 'text-right justify-end' : 'text-left justify-start';
  return (
    <th className={`px-3 py-2.5 text-[11px] font-medium uppercase tracking-wide ${stickyCls}`}>
      <button
        onClick={onClick}
        className={`inline-flex w-full items-center gap-1 transition-colors duration-150 hover:text-text ${alignCls} ${
          active ? 'text-text' : 'text-text2'
        }`}
      >
        {label}
        <span className={`text-[9px] ${active ? 'opacity-100' : 'opacity-40'}`}>
          {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

function ChatRow({
  row,
  ccys,
  spark,
  onOpen,
}: {
  row: Row;
  ccys: CcyCode[];
  spark: number[] | undefined;
  onOpen: () => void;
}) {
  const net = netView(row);
  return (
    <tr
      // Не открываем модалку, если пользователь выделял текст в строке (копирование кода аккаунта и т.п.).
      onClick={() => {
        if (window.getSelection()?.toString()) return;
        onOpen();
      }}
      className="cursor-pointer border-b border-border-subtle last:border-b-0 transition-colors duration-150 hover:bg-bg3"
    >
      <td className="sticky left-0 z-[1] bg-bg2 px-3 py-2.5">
        <div className="flex items-center gap-1.5 font-medium text-text">
          <span>{row.title}</span>
          <ChatBadges row={row} />
        </div>
        {row.accountCode && (
          <div className="mt-0.5 font-mono text-[11px] text-accent">{row.accountCode}</div>
        )}
      </td>
      <td className="px-3 py-2.5">{kindBadge(row) ?? <span className="text-text-subtle">—</span>}</td>
      <td className="w-28 px-3 py-2.5">
        {spark && spark.length >= 2 ? (
          <div title="Net USDT за 7 дней">
            <Sparkline values={spark} color={spark[spark.length - 1]! < 0 ? 'success' : 'danger'} height={24} showBaseline />
          </div>
        ) : (
          <span className="text-text-subtle">—</span>
        )}
      </td>
      {ccys.map((c) => {
        const v = row.ccyMap.get(c);
        if (!v) {
          return (
            <td key={c} className="px-3 py-2.5 text-right font-mono text-text-subtle">
              —
            </td>
          );
        }
        const staleH = row.staleByCcy.get(c);
        return (
          <td key={c} className={`px-3 py-2.5 text-right font-mono font-semibold tabular-nums ${ccyCls(row, v)}`}>
            {fmtAmt(v, c)}
            {staleH !== undefined && <StaleBadge hours={staleH} />}
          </td>
        );
      })}
      <td className={`px-3 py-2.5 text-right font-mono font-bold tabular-nums ${net.cls}`}>{net.content}</td>
    </tr>
  );
}

// ============================================================================
// Общие куски строки чата — таблица (десктоп) и карточки (телефон) рендерят
// одно и то же, чтобы мобильная версия не теряла ни бейджей, ни цветов.
// ============================================================================

const badgeCls =
  'rounded border border-border bg-bg3 px-1 py-px text-[9px] font-medium uppercase tracking-wide text-text2';

function ChatBadges({ row }: { row: Row }) {
  return (
    <>
      {row.inverted && (
        <span className={badgeCls} title="Знаки инвертированы согласно настройке чата">
          ⇄ инв
        </span>
      )}
      {row.source === 'telegram' && (
        <span
          className={badgeCls}
          title={`Баланс из Telegram-сообщений${
            row.tgUpdatedAt ? ` · обновлено ${fmtAgo(row.tgUpdatedAt, Date.now()) ?? ''}` : ''
          }`}
        >
          TG
        </span>
      )}
      {row.balanceSource !== null && row.balanceSource !== 'auto' && (
        <span
          className="rounded border border-[var(--yellow-border)] bg-[var(--yellow-bg)] px-1 py-px text-[9px] font-medium uppercase tracking-wide text-warning"
          title={
            row.balanceSource === 'telegram'
              ? 'Источник остатков задан вручную: только разбор Telegram-сообщений, цифры внешнего API игнорируются'
              : 'Источник остатков задан вручную: только внешний API, разобранные из сообщений цифры игнорируются'
          }
        >
          ⚙ {row.balanceSource === 'telegram' ? 'TG' : 'API'}
        </span>
      )}
      {row.source === 'tron' && (
        <span
          className={badgeCls}
          title={`Баланс кошелька из TronScan${
            row.tgUpdatedAt ? ` · обновлено ${fmtAgo(row.tgUpdatedAt, Date.now()) ?? ''}` : ''
          }`}
        >
          TRON
        </span>
      )}
    </>
  );
}

/** Бейдж типа (Кошелёк / Клиент / Исполнитель); null — тип не определён. */
function kindBadge(row: Row): ReactNode | null {
  if (row.source === 'tron') {
    return (
      <span className="inline-flex items-center rounded border border-border bg-bg3 px-1.5 py-0.5 text-[11px] font-medium text-text2">
        Кошелёк
      </span>
    );
  }
  if (!row.accountKind) return null;
  return (
    <span
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium ${
        row.accountKind === 'client'
          ? 'border-[var(--accent-border)] bg-[var(--accent-bg)] text-accent'
          : 'border-[var(--green-border)] bg-[var(--green-bg)] text-success'
      }`}
    >
      {row.accountKind === 'client' ? 'Клиент' : 'Исполнитель'}
    </span>
  );
}

/**
 * Цвет суммы валюты в строке чата. Кошельки — активы, не обязательства чата:
 * цвет нейтральный, знак ± не несёт семантики «должны/нам должны».
 */
function ccyCls(row: Row, v: Money): string {
  if (row.source === 'tron') return 'text-text';
  return D(v).isNegative() ? 'text-success' : 'text-danger';
}

/** Net USDT строки: класс цвета + содержимое (прочерк / «нет курса» / знак+модуль). */
function netView(row: Row): { cls: string; content: ReactNode } {
  const netD = D(row.netUsdt);
  const noRate = row.missingRateCcys.length > 0;
  const cls =
    row.source === 'tron'
      ? 'text-text'
      : noRate
        ? 'text-text-subtle'
        : netD.isNegative()
          ? 'text-success'
          : netD.isPositive()
            ? 'text-danger'
            : 'text-text-subtle';
  if (noRate) {
    return {
      cls,
      content: (
        <span
          className="inline-flex items-center gap-1"
          title={`Net USDT не рассчитан: нет курса USDT для ${row.missingRateCcys.join(', ')}. Задайте курс на /admin/rates.`}
        >
          <span>—</span>
          <span className="rounded border border-[var(--orange-border,#fdba74)] bg-[var(--orange-bg,#fff7ed)] px-1 py-px font-sans text-[9px] font-medium uppercase tracking-wide text-warning">
            нет курса
          </span>
        </span>
      ),
    };
  }
  // Знак всегда явный: formatUsdtNum отдаёт модуль, цвет знак не заменяет.
  return { cls, content: fmtSignedUsdt(row.netUsdt) };
}

function StaleBadge({ hours }: { hours: number }) {
  return (
    <span
      // Токены проекта: --yellow-bg/--yellow-border + text-warning
      // (= var(--yellow)). Никаких --orange-*: таких переменных в
      // styles/index.css нет, класс молча падал бы на хардкод-фолбэк.
      className="ml-1 rounded border border-[var(--yellow-border)] bg-[var(--yellow-bg)] px-1 py-px font-sans text-[9px] font-medium text-warning"
      title={`Значение не обновлялось ${hours} ч, тогда как другие валюты чата обновились — возможно, залипло`}
    >
      {hours}ч
    </span>
  );
}

/** «+1 234,5» / «−1 234,5» / «—» для signed USDT. */
function fmtSignedUsdt(v: string): string {
  const d = D(v);
  if (d.isZero()) return '—';
  return `${d.gte(0) ? '+' : '−'}${formatUsdtNum(v)}`;
}

/** Цвет итоговой суммы по чатам: минус («нам должны») — зелёный, плюс — красный. */
function totalCls(v: string): string {
  const d = D(v);
  return d.isZero() ? 'text-text-subtle' : d.isNegative() ? 'text-success' : 'text-danger';
}

/** Карточка чата для телефона — все поля строки таблицы, без горизонтального скролла. */
function ChatCard({
  row,
  ccys,
  spark,
  onOpen,
}: {
  row: Row;
  ccys: CcyCode[];
  spark: number[] | undefined;
  onOpen: () => void;
}) {
  const net = netView(row);
  const kind = kindBadge(row);
  // Порядок валют — как у колонок таблицы; отсутствующие (в таблице «—») не показываем.
  const present = ccys.filter((c) => row.ccyMap.has(c));
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => {
        if (window.getSelection()?.toString()) return;
        onOpen();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className="cursor-pointer rounded-lg border border-border bg-bg2 p-3 transition-colors duration-150 active:bg-bg3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 font-medium text-text">
            <span className="break-words">{row.title}</span>
            <ChatBadges row={row} />
          </div>
          {(kind || row.accountCode) && (
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {kind}
              {row.accountCode && <span className="font-mono text-[11px] text-accent">{row.accountCode}</span>}
            </div>
          )}
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[10px] font-medium uppercase tracking-wide text-text2">Net USDT</div>
          <div className={`font-mono text-base font-bold tabular-nums ${net.cls}`}>{net.content}</div>
        </div>
      </div>
      {spark && spark.length >= 2 && (
        <div className="mt-2" title="Net USDT за 7 дней">
          <div className="mb-0.5 text-[10px] uppercase tracking-wide text-text-subtle">Динамика 7д</div>
          <Sparkline values={spark} color={spark[spark.length - 1]! < 0 ? 'success' : 'danger'} height={24} showBaseline />
        </div>
      )}
      {present.length > 0 && (
        <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 border-t border-border-subtle pt-2 min-[380px]:grid-cols-2">
          {present.map((c) => {
            const v = row.ccyMap.get(c)!;
            const staleH = row.staleByCcy.get(c);
            return (
              <div key={c} className="flex min-w-0 items-baseline justify-between gap-2">
                <dt className="shrink-0 text-[11px] font-medium text-text2">{c}</dt>
                <dd className={`min-w-0 text-right font-mono text-[13px] font-semibold tabular-nums ${ccyCls(row, v)}`}>
                  {fmtAmt(v, c)}
                  {staleH !== undefined && <StaleBadge hours={staleH} />}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
    </div>
  );
}

/** «Итого (чаты)» для телефона — аналог подвала таблицы. */
function TotalsCard({ ccys, totals, net }: { ccys: CcyCode[]; totals: Map<CcyCode, string>; net: string }) {
  return (
    <div className="rounded-lg border-2 border-border bg-bg3 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-text2">Итого (чаты)</div>
        <div className="text-right">
          <div className="text-[10px] font-medium uppercase tracking-wide text-text2">Net USDT</div>
          <div className={`font-mono text-base font-bold tabular-nums ${totalCls(net)}`}>{fmtSignedUsdt(net)}</div>
        </div>
      </div>
      {ccys.length > 0 && (
        <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 border-t border-border pt-2 min-[380px]:grid-cols-2">
          {ccys.map((c) => {
            const t = totals.get(c) ?? '0';
            return (
              <div key={c} className="flex min-w-0 items-baseline justify-between gap-2">
                <dt className="shrink-0 text-[11px] font-medium text-text2">{c}</dt>
                <dd className={`min-w-0 text-right font-mono text-[13px] font-semibold tabular-nums ${totalCls(t)}`}>
                  {D(t).isZero() ? '—' : fmtAmt(t, c)}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
    </div>
  );
}

/** Сортировка на телефоне: поле + направление (на десктопе — клик по заголовку колонки). */
function MobileSort({
  sort,
  ccys,
  onChange,
}: {
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  ccys: CcyCode[];
  onChange: (s: { key: SortKey; dir: 'asc' | 'desc' }) => void;
}) {
  return (
    <div className="flex items-center gap-1 md:hidden">
      <select
        aria-label="Сортировка"
        value={sort.key}
        onChange={(e) => onChange({ key: e.target.value as SortKey, dir: sort.dir })}
        className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-text outline-none focus:border-accent"
      >
        <option value="netUsdt">Net USDT</option>
        <option value="title">Чат</option>
        {ccys.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => onChange({ key: sort.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' })}
        aria-label={sort.dir === 'asc' ? 'По возрастанию — сменить' : 'По убыванию — сменить'}
        title={sort.dir === 'asc' ? 'По возрастанию' : 'По убыванию'}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-bg text-xs text-text2 transition-colors duration-150 hover:bg-bg2"
      >
        {sort.dir === 'asc' ? '▲' : '▼'}
      </button>
    </div>
  );
}

// ============================================================================
// Empty / not-configured states
// ============================================================================

function EmptyState({ rowsCount }: { rowsCount: number }) {
  return (
    <div className="rounded-lg border border-border bg-bg2 px-6 py-12 text-center">
      {rowsCount === 0 ? (
        <>
          <div className="text-sm font-medium text-text">Нет чатов с остатком</div>
          <div className="mt-1 text-xs text-text2">Внешний источник не вернул чатов с ненулевым балансом.</div>
        </>
      ) : (
        <>
          <div className="text-sm font-medium text-text">Ничего не найдено</div>
          <div className="mt-1 text-xs text-text2">Измените фильтры или включите «Показывать пустые».</div>
        </>
      )}
    </div>
  );
}

function NotConfiguredState({
  canManage,
  onConfigure,
}: {
  canManage: boolean;
  onConfigure: () => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-bg2 px-6 py-12 text-center">
      <div className="text-sm font-medium text-text">Источник остатков не настроен</div>
      <div className="mt-1 text-xs text-text2">
        {canManage
          ? 'Укажите URL и Bearer-токен внешнего API реальных остатков по чатам.'
          : 'Обратитесь к администратору, чтобы настроить внешний источник остатков.'}
      </div>
      {canManage && (
        <button type="button" onClick={onConfigure} className={`${btnSecondarySm} mt-4`}>
          <IconSettings />
          Настроить источник
        </button>
      )}
    </div>
  );
}
